// color-exempt-file — image partagée hors de l'app (WhatsApp) : couleurs figées, indépendantes du thème.
/**
 * Carte image « C'est vendredi ! » à partager sur WhatsApp (spec S3, partie B).
 *
 * Générée dans le navigateur (canvas), jamais servie par une URL publique :
 * un sondage privé ne doit exposer son contenu dans aucun aperçu public. Par
 * prudence la carte ne porte PAS le titre du sondage — seulement le jour,
 * l'heure, des chiffres et doodates.com. Jamais de noms.
 */

export interface CardLines {
  /** « C'est vendredi ! » */
  headline: string;
  /** « 16 octobre · 19:30 » */
  when: string;
  /** « 7/8 disponibles » — optionnel. */
  count?: string;
  /** « doodates.com » */
  footer: string;
}

const SIZE = 1080;

function fitText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, start: number, min: number, weight: string): number {
  let size = start;
  while (size > min) {
    ctx.font = `${weight} ${size}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
    if (ctx.measureText(text).width <= maxWidth) break;
    size -= 4;
  }
  return size;
}

export function drawFinalDateCard(canvas: HTMLCanvasElement, lines: CardLines): void {
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const grad = ctx.createLinearGradient(0, 0, SIZE, SIZE);
  grad.addColorStop(0, "#4F46E5");
  grad.addColorStop(1, "#9333EA");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, SIZE, SIZE);

  // Pastille « coche »
  ctx.fillStyle = "rgba(255,255,255,0.18)";
  ctx.beginPath();
  ctx.arc(SIZE / 2, 250, 90, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#FFFFFF";
  ctx.lineWidth = 18;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(SIZE / 2 - 42, 252);
  ctx.lineTo(SIZE / 2 - 10, 284);
  ctx.lineTo(SIZE / 2 + 46, 216);
  ctx.stroke();

  ctx.fillStyle = "#FFFFFF";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const max = SIZE - 140;

  let size = fitText(ctx, lines.headline, max, 120, 56, "800");
  ctx.font = `800 ${size}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
  ctx.fillText(lines.headline, SIZE / 2, 480);

  size = fitText(ctx, lines.when, max, 72, 36, "600");
  ctx.font = `600 ${size}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
  ctx.fillText(lines.when, SIZE / 2, 620);

  if (lines.count) {
    ctx.fillStyle = "rgba(255,255,255,0.88)";
    size = fitText(ctx, lines.count, max, 56, 32, "500");
    ctx.font = `500 ${size}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
    ctx.fillText(lines.count, SIZE / 2, 720);
  }

  ctx.fillStyle = "rgba(255,255,255,0.75)";
  ctx.font = `600 40px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
  ctx.fillText(lines.footer, SIZE / 2, SIZE - 110);
}

export function renderFinalDateCard(lines: CardLines): Promise<Blob | null> {
  const canvas = document.createElement("canvas");
  drawFinalDateCard(canvas, lines);
  // JPEG : ~10× plus léger qu'un PNG pour ce dégradé plein cadre (≈1 Mo en
  // PNG), ce qui compte pour un envoi sur WhatsApp en données mobiles.
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.9));
}

/**
 * Partage natif (mobile : WhatsApp apparaît dans la feuille de partage) ;
 * sinon téléchargement de l'image. Renvoie le mode utilisé.
 */
export async function shareFinalDateCard(
  lines: CardLines,
  text: string,
  fileName = "doodates-date.jpg",
): Promise<"shared" | "downloaded" | "cancelled" | "failed"> {
  const blob = await renderFinalDateCard(lines);
  if (!blob) return "failed";
  const file = new File([blob], fileName, { type: "image/jpeg" });
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  if (nav.share && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ files: [file], text });
      return "shared";
    } catch (e) {
      if ((e as DOMException)?.name === "AbortError") return "cancelled";
    }
  }
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(href), 0);
  return "downloaded";
}
